import { addDays, getDay, getISOWeek, getISOWeekYear, parseISO, setISOWeek, startOfISOWeek } from 'date-fns';
import { getDatesDiffDays, getDayNameShort, isDaysEqual } from '../../utils/date';
import { FormattedMessage } from 'react-intl';
import { DateRangePicker } from 'react-date-range';
import React, { useEffect, useState } from 'react';
import memoizeOne from 'memoize-one';
import { de, enUS, fr, it } from 'date-fns/locale';
import './AvailabilityCalendar.scss';
import { createDateRange } from '../../utils/react';
import { Button, EButtonType, EInputType, Input } from '@repo/apg-ui';
import classNames from 'classnames';
import { SchemaPossibleBookingDates } from '@repo/legacy-commerce-api';

interface Props {
  handleDateChange?: any;
  dateChangeCallback?: any;
  availabilities: any;
  possibleBookingDates: SchemaPossibleBookingDates;
  dateRange: any;
  onShownDateChange?: any;
  duration: number;
  minDays?: number;
  maxDays?: number;
  intl: any;
  isMobile: boolean;
  isAnalogProduct?: boolean;
  initStep?: boolean;
}

const AvailabilityCalendar = ({
  handleDateChange,
  dateChangeCallback,
  availabilities,
  possibleBookingDates,
  dateRange,
  onShownDateChange,
  duration,
  minDays,
  maxDays,
  intl,
  isMobile,
  isAnalogProduct,
  initStep,
}: Props) => {
  const [focusedRange, setFocusedRange] = React.useState<[number, number] | undefined>([0, 0]);
  const [weeks, setWeeks] = useState<string | number>(1);
  // const [isShowCrossYearError, setIsShowCrossYearError] = useState(false);
  const [isButtonDecreaseDisabled, setIsButtonDecreaseDisabled] = useState(false);
  const [isButtonIncreaseDisabled, setIsButtonIncreaseDisabled] = useState(false);

  const MAX_BOOKABLE_DAYS_ANALOG = duration > 21 && !initStep ? 28 : 21;
  const MAX_BOOKABLE_DAYS_DIGITAL = 70;
  const MAX_BOOKABLE_DAYS = isAnalogProduct ? MAX_BOOKABLE_DAYS_ANALOG : MAX_BOOKABLE_DAYS_DIGITAL;

  const MAX_BOOKABLE_WEEKS_ANALOG = duration > 21 && !initStep ? 4 : 3;
  const MAX_BOOKABLE_WEEKS_DIGITAL = 10;
  const MAX_BOOKABLE_WEEKS = isAnalogProduct ? MAX_BOOKABLE_WEEKS_ANALOG : MAX_BOOKABLE_WEEKS_DIGITAL;

  const PRODUCT_MIN_BOOKABLE_DAYS = minDays ? minDays : isAnalogProduct ? 7 : 1;
  const PRODUCT_MAX_BOOKABLE_DAYS = maxDays ? maxDays : MAX_BOOKABLE_DAYS;

  const EXCEPTION_WEEK_LABEL = intl.formatMessage({
    id: 'ec.modals.digiflex.legend.threeWeeksLabel',
    defaultMessage: '3 und mehr',
  });

  useEffect(() => {
    if (dateRange.length > 0 && isAnalogProduct) {
      const duration = getDatesDiffDays(dateRange[0].startDate, dateRange[0].endDate);
      const newWeeks = Math.round(duration / 7);
      if (newWeeks === 3) {
        setWeeks(EXCEPTION_WEEK_LABEL);
      } else if (newWeeks > MAX_BOOKABLE_WEEKS) {
        setWeeks(MAX_BOOKABLE_WEEKS);
      } else {
        setWeeks(newWeeks);
      }

      // Decrease/increase buttons
      newWeeks === 1 ? setIsButtonDecreaseDisabled(true) : setIsButtonDecreaseDisabled(false);
      newWeeks === 3 || newWeeks > MAX_BOOKABLE_WEEKS
        ? setIsButtonIncreaseDisabled(true)
        : setIsButtonIncreaseDisabled(false);

      duration === PRODUCT_MIN_BOOKABLE_DAYS ? setIsButtonDecreaseDisabled(true) : setIsButtonDecreaseDisabled(false);
      duration === PRODUCT_MAX_BOOKABLE_DAYS ? setIsButtonIncreaseDisabled(true) : setIsButtonIncreaseDisabled(false);
    }
  }, [dateRange]);

  const isDayDisabled = (day) => {
    let weekNo = getISOWeek(day);
    let year = getISOWeekYear(day);

    let weekNoAsString = String(weekNo);
    if (weekNo < 10) {
      weekNoAsString = '0' + weekNo;
    }

    let isDayDisabled = true;

    if (isAnalogProduct) {
      if (availabilities && availabilities.length > 0) {
        availabilities.forEach((availabilityObj) => {
          if (availabilityObj.year === String(year)) {
            if (availabilityObj.week === weekNo || (availabilityObj.weeks || []).includes(weekNoAsString)) {
              isDayDisabled = false;
            }
          }
        });
      } else {
        isDayDisabled = !initStep;
      }
      return isDayDisabled;
    }
    const dayIndex = getDay(day) == 0 ? 7 : getDay(day) + 1;
    availabilities &&
      availabilities.forEach((availabilityObj) => {
        if (availabilityObj.day === dayIndex) {
          if (availabilityObj.year === year) {
            if (weekNo === availabilityObj.week) {
              isDayDisabled = false;
            }
          }
        }
      });

    return isDayDisabled;
  };

  const getDateLimit = (time) => {
    if (!isAnalogProduct && time === 'start') {
      if (possibleBookingDates && possibleBookingDates['startdigital']) {
        return parseISO(possibleBookingDates['startdigital'].date);
      }
    }
    if (possibleBookingDates && possibleBookingDates[time]) {
      return parseISO(possibleBookingDates[time].date);
    }
    return null;
  };

  const getClassNameOfDay = (day) => {
    const weekNo = getISOWeek(day);
    const year = getISOWeekYear(day);
    const dayIndex = getDay(day) == 0 ? 7 : getDay(day);
    const dayInRange = dateRange.length > 0 ? dateRange[0].startDate <= day && day <= dateRange[0].endDate : false;
    let dayClassName = '';
    if (dateRange.length > 0) {
      if (isDaysEqual(day, dateRange[0].startDate)) {
        dayClassName += 'rdrDayRangeStart ';
      }
      if (isDaysEqual(day, dateRange[0].endDate)) {
        dayClassName += 'rdrDayRangeEnd ';
      }
      if (dayInRange) {
        dayClassName += 'rdrDayInRange ';
      }
    }
    !!availabilities &&
      availabilities.length > 0 &&
      availabilities.forEach((availabilityObj) => {
        if (
          availabilityObj.day == dayIndex &&
          weekNo == availabilityObj.week &&
          availabilityObj.year == year &&
          availabilityObj.rating < 0
        ) {
          dayClassName += 'rdrDayStroke';
        }
      });
    return dayClassName;
  };

  const memoizedGetClassNameOfDay = memoizeOne(getClassNameOfDay);

  const getRangeStartDatePretty = () => {
    if (dateRange.length > 0) {
      const weekDay = getDayNameShort(dateRange[0].startDate, intl.locale);
      const date = `${dateRange[0].startDate.getDate()}.${dateRange[0].startDate.getMonth() + 1}.${getISOWeekYear(
        dateRange[0].startDate,
      )}`;
      return `${weekDay}., ${date}`;
    }
    return '-';
  };

  const getRangeEndDatePretty = () => {
    if (dateRange.length === 0 || !dateRange[0]?.endDate) {
      return '-';
    }
    const weekDay = getDayNameShort(dateRange[0].endDate, intl.locale);
    const date = `${dateRange[0].endDate.getDate()}.${dateRange[0].endDate.getMonth() + 1}.${getISOWeekYear(
      dateRange[0].endDate,
    )}`;
    return `${weekDay}., ${date}`;
  };

  const getCalendarLocale = () => {
    if (intl.locale === 'de') {
      return de;
    } else if (intl.locale === 'en') {
      return enUS;
    } else if (intl.locale === 'fr') {
      return fr;
    } else {
      return it;
    }
  };

  const changeWeeks = (isIncrease: boolean) => {
    const boundaryValue = isIncrease ? MAX_BOOKABLE_WEEKS : 1;
    const changedValue = isIncrease ? Number(weeks) + 1 : Number(weeks) - 1;
    const newWeeksValue = weeks === boundaryValue ? boundaryValue : changedValue;
    if (newWeeksValue === 3) {
      setWeeks(EXCEPTION_WEEK_LABEL);
    } else if (newWeeksValue > MAX_BOOKABLE_WEEKS) {
      setWeeks(MAX_BOOKABLE_WEEKS);
    } else {
      setWeeks(newWeeksValue);
    }
    const endDate = dateRange[0].endDate;
    const updatedEndDate = isIncrease ? endDate.getDate() + 7 : endDate.getDate() - 7;
    endDate.setDate(updatedEndDate);

    onChange({ selection: { startDate: dateRange[0].startDate, endDate: endDate } });
  };

  const onInputChange = (_value: number | string) => {
    const endDate = dateRange[0].endDate;
    if (_value === EXCEPTION_WEEK_LABEL) {
      _value = 3;
    }
    const updatedEndDate = endDate.getDate() + 7 * (Number(_value) - Number(weeks));
    endDate.setDate(updatedEndDate);

    onChange({ selection: { startDate: dateRange[0].startDate, endDate: endDate } });
  };

  const onChange = (item) => {
    // If we have a callback, we don't need to call the handleDateChange method
    if (dateChangeCallback) {
      let newDuration;
      const startDate = item.selection.startDate;

      const dayIndex = getDay(startDate) == 0 ? 7 : getDay(startDate);
      if (dayIndex !== 0) {
        startDate.setDate(startDate.getDate() - dayIndex + 1);
      }

      newDuration = getDatesDiffDays(item.selection.endDate, startDate) + 1;

      if (newDuration < PRODUCT_MIN_BOOKABLE_DAYS) {
        newDuration = PRODUCT_MIN_BOOKABLE_DAYS;
      }

      const durationExtraDays = newDuration % 7;
      if (newDuration < 7) {
        newDuration = 7;
      } else if (durationExtraDays !== 0) {
        newDuration = newDuration - durationExtraDays;
        if (newDuration != PRODUCT_MAX_BOOKABLE_DAYS && durationExtraDays > 3) {
          newDuration = newDuration + 7;
        }
      }

      if (newDuration > PRODUCT_MAX_BOOKABLE_DAYS) {
        newDuration = PRODUCT_MAX_BOOKABLE_DAYS;
      }

      const range = createDateRange(startDate, newDuration, {
        toEndOfYear: false,
        fullWeeks: true,
      });

      dateChangeCallback(range, newDuration);
    } else {
      handleDateChange(item.selection.startDate, item);
    }
  };

  return (
    <>
      <div
        className={classNames('w-100 pb-4 border availability-calendar', {
          'px-48': !isMobile,
          'px-3': isMobile,
        })}
      >
        {isAnalogProduct && (
          <div
            className={classNames('d-flex border align-items-center justify-content-center', {
              'm-3 px-4 py-2 flex-column': isMobile,
              'm-4 px-48 py-4 flex-row': !isMobile,
            })}
          >
            {isMobile && (
              <div className='font-16 font-bold text-dark mb-4 text-center mt-3'>
                <FormattedMessage
                  id='product.availability.calendar.selectWeeksAndChooseDate'
                  defaultMessage='Select number of weeks and choose a start date.'
                />
              </div>
            )}
            <div className='d-flex align-items-center justify-content-center mb-3 flex-column flex-md-row '>
              <Button
                buttonType={EButtonType.InvertedPillMedium}
                className={'mt-0 mx-24 weeks-buttons weeks-buttons--decrease'}
                onClick={() => changeWeeks(false)}
                isDisabled={isButtonDecreaseDisabled}
              >
                -
              </Button>
              <Input
                type={EInputType.Text}
                inputWrapperClass={'mt-0 weeks-input py-5 py-md-0 no-pointer-events'}
                value={weeks}
                min={1}
                max={MAX_BOOKABLE_WEEKS}
                onChange={(name, value) => {
                  if (value === 3) {
                    value = EXCEPTION_WEEK_LABEL;
                  }
                  onInputChange(value);
                }}
              ></Input>

              <Button
                buttonType={EButtonType.InvertedPillMedium}
                className={'mt-0 mx-24 weeks-buttons weeks-buttons--increase'}
                onClick={() => changeWeeks(true)}
                isDisabled={isButtonIncreaseDisabled}
              >
                +
              </Button>
              {!isMobile && (
                <div className='font-14'>
                  <FormattedMessage id='product.availability.calendar.weeks' defaultMessage='Woche(n)' />
                </div>
              )}
            </div>
          </div>
        )}
        <DateRangePicker
          onChange={onChange}
          showSelectionPreview={true}
          disabledDay={(day) => {
            return isDayDisabled(day);
          }}
          dayClassName={(day) => memoizedGetClassNameOfDay(day)}
          moveRangeOnFirstSelection={false}
          showMonthAndYearPickers={false}
          onShownDateChange={onShownDateChange ? onShownDateChange : () => {}}
          ranges={dateRange}
          className='custom-date-range-calendar products-calendar w-100 digiflex-calendar'
          monthDisplayFormat='MMMM yyyy'
          direction='horizontal'
          rangeColors={['#FDF6D5']}
          color='#FDF6D5'
          focusedRange={[0, 0]}
          weekStartsOn={1}
          locale={getCalendarLocale()}
          months={isMobile ? 1 : 2}
          minDate={getDateLimit('start')}
          maxDate={getDateLimit('end')}
          onClickWeekNumber={(event, weekNumber, year) => {
            // ISO 8601: January 4 is always in week 1 of the ISO year.
            const startDate = startOfISOWeek(setISOWeek(new Date(year, 0, 4), weekNumber));
            let extraDays;
            if (weeks === EXCEPTION_WEEK_LABEL) {
              extraDays = 18;
            } else {
              extraDays = Number(weeks) * 7;
            }

            const endDate = addDays(startDate, extraDays - 1);
            const item = {
              selection: {
                startDate: startDate,
                endDate: endDate,
              },
            };
            onChange(item);
          }}
        />
        <div className='d-flex flex-row w-100 mt-24'>
          <div className='d-flex font-12'>
            <span className='green-dot me-2'></span>
            <FormattedMessage id='ec.modals.digiflex.legend.available' defaultMessage='Verfügbar' />
          </div>
          {!isAnalogProduct && (
            <>
              <div className='d-flex font-12 px-24'>
                <span className='red-dot me-2'></span>
                <FormattedMessage
                  id='ec.modals.digiflex.legend.notAvailable'
                  defaultMessage='Keine Verfügbarkeit für Ihr Budget'
                />
              </div>

              <div className='d-flex font-12 px-24'>
                <span className='gray-dot me-2'></span>
                <FormattedMessage
                  id='ec.modals.digiflex.legend.disabled'
                  defaultMessage='Ausserhalb buchbarem Zeitraum'
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className='py-3 font-14'>
        {duration}{' '}
        {intl.formatMessage({
          id: 'ec.journey01.days',
          defaultMessage: 'Tage',
        })}{' '}
        | {getRangeStartDatePretty()} - {getRangeEndDatePretty()}
      </div>
      {/*{isShowCrossYearError && (*/}
      {/*    <div className="py-3 px-24 font-14 bg-yellow varied-warning mb-3">*/}
      {/*        <b className="me-2">*/}
      {/*            <FormattedMessage id="ec.modals.digiflex.note" defaultMessage="Note" />*/}
      {/*        </b>*/}
      {/*        <FormattedMessage*/}
      {/*            id="ec.modals.digiflex.timeslotweekly.crossYearError"*/}
      {/*            defaultMessage="Sie haben eine jahresübergreifende Zeitspanne ausgewählt.*/}
      {/*                              Dies ist aktuell so nicht möglich. Bitte wählen Sie eine andere Zeitspanne aus."*/}
      {/*        />*/}
      {/*    </div>*/}
      {/*)}*/}
    </>
  );
};

export default AvailabilityCalendar;
